import { GeoSearch } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.js';
import MapBox from '../../map/MapBox.js';

type Props = {
  mapRef: React.RefObject<MapBox>;
  allowPolygonDraw: boolean;
  geoSearch: GeoSearch;
  mapLoaded: boolean;
  subjectLat: number;
  subjectLng: number;
};

export function useRedrawPolygon({
  mapRef,
  allowPolygonDraw,
  geoSearch,
  mapLoaded,
  subjectLat,
  subjectLng,
}: Props) {
  useEffect(() => {
    if (!mapRef.current || !allowPolygonDraw || !mapLoaded) return;
    const mapBox = mapRef.current;
    const map = mapBox.getMap();
    const draw = mapBox.getDraw();
    if (!map.hasControl(draw as any)) return;

    const { getGeoCoords, disablePolygonDrawButton, enableTrashButton, enablePolygonDrawButton } =
      mapBox;

    if (geoSearch.t === 'polygon') {
      const coordinates = geoSearch.coords;

      const currentPolygons = getGeoCoords();
      if (currentPolygons.length > 1) {
        throw new Error('Multiple polygons not supported');
      }

      const currentPolygon = currentPolygons[0];
      const shouldRedrawPolygon =
        (!currentPolygon?.length || !currentPolygon[0]) && !!coordinates.length;

      if (shouldRedrawPolygon) {
        disablePolygonDrawButton();
        enableTrashButton();

        draw.deleteAll();
        draw.add({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {},
        });
      } else if (currentPolygon && currentPolygon.length > 0) {
        draw.changeMode('simple_select');
        disablePolygonDrawButton();
      } else {
        draw.changeMode('draw_polygon');
        enablePolygonDrawButton();
      }
    } else {
      draw.deleteAll();
      disablePolygonDrawButton();
    }
  }, [allowPolygonDraw, geoSearch, mapLoaded, mapRef]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    if (geoSearch.t !== 'polygon' || !geoSearch.coords.length) return;

    fitBounds(mapRef.current.getMap(), [
      { latitude: subjectLat, longitude: subjectLng },
      ...geoSearch.coords.map(([lng, lat]) => ({
        latitude: lat,
        longitude: lng,
      })),
    ]);
  }, [mapLoaded, geoSearch, subjectLat, subjectLng]);
}
